import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const DEFAULT_NOTES_KEY = "invoice_default_notes";

export default function NotesSection({ invoice, updateField, disabled = false }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [useDefault, setUseDefault] = useState(false);

  // Load default notes from localStorage
  useEffect(() => {
    if (disabled) return;
    const defaultNotes = localStorage.getItem(DEFAULT_NOTES_KEY);
    if (defaultNotes && !invoice.clientNotes && useDefault) {
      updateField("clientNotes", defaultNotes);
    }
  }, [useDefault, disabled]);

  // Save to default when checkbox is checked and notes exist
  function handleDefaultToggle(checked) {
    setUseDefault(checked);
    if (checked && invoice.clientNotes) {
      localStorage.setItem(DEFAULT_NOTES_KEY, invoice.clientNotes);
    }
  }

  // Auto-save to default when notes change and checkbox is checked
  useEffect(() => {
    if (useDefault && invoice.clientNotes) {
      localStorage.setItem(DEFAULT_NOTES_KEY, invoice.clientNotes);
    }
  }, [invoice.clientNotes, useDefault]);

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-2xl font-light text-slate-900">Notes</h2>
      </div>

      {/* Notes Container */}
      <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-[1px] transition-all duration-200">
        {/* Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50/70 active:bg-slate-100/50 transition-all duration-150"
        >
          <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 transition-colors">
            Invoice Notes
          </span>
          {isExpanded ? <ChevronUp size={16} className="text-slate-400 transition-all duration-200" /> : <ChevronDown size={16} className="text-slate-400 transition-all duration-200" />}
        </button>

        {/* Content */}
        {isExpanded && (
          <div className="px-6 pb-6 space-y-4">
            {/* Notes to Client */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs text-slate-500 uppercase tracking-wide">
                  Notes to Client
                </label>
                {/* Default Notes Toggle */}
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={useDefault}
                    onChange={(e) => handleDefaultToggle(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 focus:ring-2"
                  />
                  <span className="text-xs text-slate-600 group-hover:text-slate-900 transition">
                    Use as default
                  </span>
                </label>
              </div>
              <textarea
                value={invoice.clientNotes || ""}
                onChange={(e) => !disabled && updateField("clientNotes", e.target.value)}
                disabled={disabled}
                placeholder="Add any notes you want the client to see..."
                className={`w-full text-sm bg-slate-50 border-0 rounded-lg px-4 py-3 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 resize-none transition-all duration-150 ${
                  disabled ? "opacity-50 cursor-not-allowed" : ""
                }`}
                rows={3}
              />
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  {useDefault && (
                    <span className="inline-flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                      Saved as default
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 text-right">
                  {(invoice.clientNotes || "").length} characters
                </div>
              </div>
            </div>

            {/* Private Notes */}
            <div className="space-y-2">
              <label className="text-xs text-slate-500 uppercase tracking-wide">
                Private Notes
              </label>
              <textarea
                value={invoice.privateNotes || ''}
                onChange={(e) => !disabled && updateField("privateNotes", e.target.value)}
                disabled={disabled}
                placeholder="Add internal notes (follow-up reminders, special instructions, team notes)..."
                rows={4}
                className={`w-full px-4 py-3 text-sm bg-slate-50 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 resize-none transition-all duration-150 ${
                  disabled ? "opacity-50 cursor-not-allowed" : ""
                }`}
              />
              <div className="text-xs text-slate-400">
                These notes are for internal use only and will not be shared with the client.
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200">
              <div className="text-xs text-slate-500">
                {(invoice.clientNotes || invoice.privateNotes) ? "Notes added" : "No notes added"}
              </div>
              <div className="flex gap-2">
                {(invoice.clientNotes || invoice.privateNotes) && (
                  <button
                    onClick={() => {
                      updateField("clientNotes", "");
                      updateField("privateNotes", "");
                    }}
                    className="px-3 py-1 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-all duration-150 active:scale-95"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
